import { defineStore } from 'pinia'
import SettingsService from '@/api/SettingsService.js'

export const useSettingsStore = defineStore('settings', {
    state: () => ({
        emailsAndCompliance: {
            'compliance-privacy_data_policy_link_en': '',
            'compliance-privacy_data_policy_link_de': '',
            'compliance-GTC_link_de': '',
            'compliance-GTC_link_en': '',
            'mail-data_export_insurance': '',
            'mail-invoice': '',
            'mail-kyc_report_to_officer': '',
            'mail-kyc_unremarkable_transaction': '',
            'mail-lessee': '',
            'mail-oem': '',
            'mail-operations': '',
            'mail-platform': '',
            'mail-sales': '',
            'mail-management': '',
            'mail-send_copy_to': '',
            'mail-invoice_send_to': '',
        },

        contractApproval: {
            'contract_approval-min_financing_volume': null,
            'contract_approval-max_financing_volume': null,
            'contract_approval-min_duration_months': null,
            'contract_approval-max_duration_months': null,
            'contract_approval-allowed_customization_degrees': null,
            'contract_approval-max_lessee_volume': null,
            'contract_approval-max_dnb_pd': null, // percent
        },

        creditRisk: {
            'credit_risk-financial_strength_red_yellow': '',
            'credit_risk-financial_strength': '',
            'credit_risk-risk_indicator_red_yellow': 0,
            'credit_risk-risk_indicator_yellow_green': 0,
            'credit_risk-paydex_red_yellow': 0,
            'credit_risk-paydex_yellow_green': 0,
            'credit_risk-delinquency_red_yellow': 0,
            'credit_risk-delinquency_yellow_green': 0,
        },
        creditRiskNonSettings: {
            financial_strength_options: [],
            financial_strength_info: [],
        },

        invoice: {
            'invoice-fee_annual_contract_admin': null, // percent
            'invoice-fee_oneoff_contract_handling': null, //percent
            'invoice-fee_overdue_payment': null, // num
            'invoice-insurance_fee': null, // percent
            'invoice-insurance_stationary': null, // percent
            'invoice-insurance_movable': null, // percent
            'invoice-stamp_duty_months': null, // num
            'invoice-stamp_duty_perc': null, // percent
            'invoice-payment_term_standard': null, // num
            'invoice-grace_period1': null, // num
            'invoice-grace_period2': null, // num
            'invoice-grace_period3': null, // num
        },

        calculator: {
            'calculator-annual_refinancing_rate': null, // percent
            'calculator-break_even': null,              // percent
        },

        caf: {
            'pricing-breakeven_full': null, // percent
            'pricing-breakeven_high': null, // percent
            'pricing-breakeven_medium': null, // percent
            'pricing-breakeven_low': null, // percent
            'pricing-funding_spread': null, // percent
            'pricing-min_carry_full': null, // percent
            'pricing-min_carry_high': null, // percent
            'pricing-min_carry_medium': null, // percent
            'pricing-min_carry_low': null, // percent
            'pricing-ppu_fee_decimals_threshold': null, // num
            'pricing-residual_value_day1_percent': null, // percent
            'pricing-residual_value_end_percent': null, // percent
            'caf-funding_tenor_minimum': null, // num
            'caf-funding_tenor_maximum': null, // num
        },

        caf_oem: {
            'pricing-breakeven_full': null, // percent
            'pricing-breakeven_high': null, // percent
            'pricing-breakeven_medium': null, // percent
            'pricing-breakeven_low': null, // percent
            'pricing-funding_spread': null, // percent
            'pricing-min_carry_full': null, // percent
            'pricing-min_carry_high': null, // percent
            'pricing-min_carry_medium': null, // percent
            'pricing-min_carry_low': null, // percent
            'pricing-ppu_fee_decimals_threshold': null, // num
            'pricing-residual_value_day1_percent': null, // percent
            'pricing-residual_value_end_percent': null, // percent
            'caf-funding_tenor_minimum': null, // num
            'caf-funding_tenor_maximum': null, // num
        },
        oem_list: {},
        roles_list: [],

        selected_oem: 0,

        loadingOemList: false,
        loading: false,
        saving_caf: false,
        saving_caf_oem: false,
        saving_calculator: false,
        saving_contractApproval: false,
        saving_creditRisk: false,
        saving_emailsAndCompliance: false,
        saving_invoice: false,
        saving_permission_change: false,
        saving_role: false,
        updating_role: false,
        deleting_role: false,
    }),

    getters: {},

    actions: {
        setOem(oem) {
            this.selected_oem = oem
            this.getSettingsData('admin.click-and-finance-settings.show_oem', 'caf_oem', { oem })
        },

        notification(severity, detail) {
            const summary = severity === 'error' ? 'Error' : 'Success'
            this.toast.add({
                severity,
                summary,
                detail,
                life: this.getToastDuration(detail, severity),
            })
        },

        resetEmailsAndCompliance() {
            this.emailsAndCompliance = {
                'compliance-privacy_data_policy_link_en': '',
                'compliance-privacy_data_policy_link_de': '',
                'compliance-GTC_link_de': '',
                'compliance-GTC_link_en': '',
                'mail-data_export_insurance': '',
                'mail-invoice': '',
                'mail-kyc_report_to_officer': '',
                'mail-kyc_unremarkable_transaction': '',
                'mail-lessee': '',
                'mail-oem': '',
                'mail-operations': '',
                'mail-platform': '',
                'mail-sales': '',
                'mail-management': '',
                'mail-send_copy_to': '',
                'mail-invoice_send_to': '',
            }
        },

        resetContractApproval() {
            this.contractApproval = {
                'contract_approval-min_financing_volume': null,
                'contract_approval-max_financing_volume': null,
                'contract_approval-min_duration_months': null,
                'contract_approval-max_duration_months': null,
                'contract_approval-allowed_customization_degrees': null,
                'contract_approval-max_lessee_volume': null,
                'contract_approval-max_dnb_pd': null, // percent
            }
        },

        resetCreditRisk() {
            this.creditRisk = {
                'credit_risk-financial_strength_red_yellow': '',
                'credit_risk-financial_strength': '',
                'credit_risk-risk_indicator_red_yellow': 0,
                'credit_risk-risk_indicator_yellow_green': 0,
                'credit_risk-paydex_red_yellow': 0,
                'credit_risk-paydex_yellow_green': 0,
                'credit_risk-delinquency_red_yellow': 0,
                'credit_risk-delinquency_yellow_green': 0,
            }
        },

        resetInvoice() {
            this.invoice = {
                'invoice-fee_annual_contract_admin': null, // percent
                'invoice-fee_oneoff_contract_handling': null, // percent
                'invoice-fee_overdue_payment': null, // num
                'invoice-insurance_fee': null, // percent
                'invoice-insurance_stationary': null, // percent
                'invoice-insurance_movable': null, // percent
                'invoice-stamp_duty_months': null, // num
                'invoice-stamp_duty_perc': null, // percent
                'invoice-payment_term_standard': null, // num
                'invoice-grace_period1': null, // num
                'invoice-grace_period2': null, // num
                'invoice-grace_period3': null, // num
            }
        },

        resetCalculator() {
            this.calculator = {
                'calculator-annual_refinancing_rate': null, // percent
                'calculator-break_even': null,              // percent
            }
        },

        resetCaf() {
            this.caf = {
                'pricing-breakeven_full': null, // percent
                'pricing-breakeven_high': null, // percent
                'pricing-breakeven_medium': null, // percent
                'pricing-breakeven_low': null, // percent
                'pricing-funding_spread': null, // percent
                'pricing-min_carry_full': null, // percent
                'pricing-min_carry_high': null, // percent
                'pricing-min_carry_medium': null, // percent
                'pricing-min_carry_low': null, // percent
                'pricing-ppu_fee_decimals_threshold': null, // num
                'pricing-residual_value_day1_percent': null, // percent
                'pricing-residual_value_end_percent': null, // percent
                'caf-funding_tenor_minimum': null, // num
                'caf-funding_tenor_maximum': null, // num
            }
        },

        resetCafOem() {
            this.caf_oem = {
                'pricing-breakeven_full': null, // percent
                'pricing-breakeven_high': null, // percent
                'pricing-breakeven_medium': null, // percent
                'pricing-breakeven_low': null, // percent
                'pricing-funding_spread': null, // percent
                'pricing-min_carry_full': null, // percent
                'pricing-min_carry_high': null, // percent
                'pricing-min_carry_medium': null, // percent
                'pricing-min_carry_low': null, // percent
                'pricing-ppu_fee_decimals_threshold': null, // num
                'pricing-residual_value_day1_percent': null, // percent
                'pricing-residual_value_end_percent': null, // percent
                'caf-funding_tenor_minimum': null, // num
                'caf-funding_tenor_maximum': null, // num
            }
        },

        resetOemList() {
            this.oem_list = {}
        },

        async getOemList() {
            this.loadingOemList = true
            try {
                const { data } = await SettingsService.getLists('admin.oem_list')
                this.oem_list = data
            } catch (error) {
                this.notification('error', '§ui_message.fetching_failed')
                console.error('Error fetching oem list (stores/settings/getOemList):', error)
            } finally {
                this.loadingOemList = false
            }
        },

        async getSettingsData(route, group, params = {}) {
            this.loading = true
            try {
                const data = await SettingsService.get(route, params)
                if (group === 'creditRisk') {
                    this['creditRisk'] = { ...data.settings }
                    this['creditRiskNonSettings'] = {
                        financial_strength_options: data.financial_strength_options,
                        financial_strength_info: data.financial_strength_info,
                    }
                } else {
                    this[group] = { ...data }
                }
            } catch (error) {
                this.notification('error', '§ui_message.fetching_failed')
                console.error(
                    'Error fetching settings data (stores/settings/getSettingsData):',
                    error
                )
            } finally {
                this.loading = false
            }
        },

        async update(route, group, params = {}) {
            if (this[`saving_${group}`]) return

            this[`saving_${group}`] = true
            try {
                await SettingsService.update(route, this[group], params)
                this.notification('success', '§ui_message.update_successful')
            } catch (error) {
                this.notification('error', '§ui_message.update_failed')
                console.error(
                    `Error updating ${group} settings data (stores/settings/update):`,
                    error
                )
            } finally {
                this[`saving_${group}`] = false
            }
        },

        async getRolesList() {
            this.loading = true
            try {
                const { data } = await SettingsService.getLists('admin.roles_list')
                this.roles_list = data
            } catch (error) {
                this.notification('error', '§ui_message.fetching_failed')
                console.error('Error fetching roles list (stores/settings/getRolesList):', error)
            } finally {
                this.loading = false
            }
        },

        async togglePermission(role_id, data) {
            if (this.saving_permission_change) return

            this.saving_permission_change = true
            try {
                await SettingsService.togglePermission({ role_id }, data)
                this.notification('success', '§ui_message.update_successful')
                this.getRolesList()
            } catch (error) {
                this.notification('error', '§ui_message.save_failed')
                console.error(
                    'Error toggling permission (stores/settings/togglePermission):',
                    error
                )
            } finally {
                this.saving_permission_change = false
            }
        },

        async createRole(data) {
            if (this.saving_role) return

            this.saving_role = true
            try {
                await SettingsService.createRole(data)
                this.notification('success', '§ui_message.save_successful')
                this.getRolesList()
            } catch (error) {
                this.notification('error', '§ui_message.save_failed')
                console.error('Error saving role (stores/settings/createRole):', error)
            } finally {
                this.saving_role = false
            }
        },

        async updateRole(role_id, data) {
            if (this.updating_role) return

            this.updating_role = true
            try {
                await SettingsService.updateRole({ role_id }, data)
                this.notification('success', '§ui_message.save_successful')
                this.getRolesList()
            } catch (error) {
                this.notification('error', '§ui_message.save_failed')
                console.error('Error updating role (stores/settings/updateRole):', error)
            } finally {
                this.updating_role = false
            }
        },

        async deleteRole(role_id) {
            if (this.deleting_role) return

            this.deleting_role = true
            try {
                await SettingsService.deleteRole({ role_id })
                this.notification('success', '§ui_message.delete_successful')
                this.getRolesList()
            } catch (error) {
                this.notification('error', '§ui_message.delete_failed')
                console.error('Error updating role (stores/settings/deleteRole):', error)
            } finally {
                this.deleting_role = false
            }
        },
    },
})
